import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';

type CallRow = {
  id: string;
  callerName?: string | null;
  callerNumber?: string | null;
  urgencyLevel?: string | null;
  startedAt?: string | null;
  createdAt?: string | null;
  // Auto-linked contact for this caller's number (§4.3). Its saved name is the one
  // the customer recognises, so it wins over callerName (what the caller told the
  // agent) for the row title.
  contact?: { name?: string | null } | null;
};

export function urgencyClass(u?: string | null): string {
  switch (u) {
    case 'URGENT':
      return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300';
    case 'HIGH':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/** One account's calls as a plain vertical list — one call per row. A tap fires
 *  `onOpen(id)`: the customer screen navigates to a full detail page (mobile),
 *  the admin hub selects it into a side panel. `activeId` highlights the current
 *  row when a panel is shown alongside. */
export function CallsList({
  accountId,
  onOpen,
  activeId,
}: {
  accountId: string;
  onOpen: (id: string) => void;
  activeId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const callsQ = crud.call.useFindMany({
    where: { accountId },
    select: {
      id: true,
      callerName: true,
      callerNumber: true,
      urgencyLevel: true,
      startedAt: true,
      createdAt: true,
      contact: { select: { name: true } },
    },
    orderBy: { createdAt: 'desc' },
    take: 100,
  });
  const calls = (callsQ.data as CallRow[] | undefined) ?? [];

  // Match callers to saved contacts by number at read time too — not just via the
  // stored contactId — so calls recorded before a contact existed (or before
  // auto-linking landed) still show the name. A single-person line has few
  // contacts, so this is one small query and an in-memory map keyed by e164.
  const contactsQ = crud.contact.useFindMany({
    where: { accountId },
    select: { e164: true, name: true },
  });
  const nameByNumber = new Map<string, string>();
  for (const c of (contactsQ.data as { e164?: string | null; name?: string | null }[] | undefined) ??
    []) {
    if (c.e164 && c.name?.trim()) nameByNumber.set(c.e164, c.name.trim());
  }
  const fmt = (d?: string | null) =>
    d
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(d),
        )
      : '—';

  if (calls.length === 0) {
    // Don't flash "no calls" before the first load resolves.
    return callsQ.isLoading ? null : (
      <p className="text-sm text-muted-foreground">{t('calls.empty')}</p>
    );
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card">
      {calls.map((c) => {
        // Contact name wins over callerName; resolve it from the stored relation
        // first, then the by-number map (covers calls linked after the fact), then
        // fall back to the raw number.
        const number = c.callerNumber || null;
        const name =
          c.contact?.name?.trim() ||
          (number ? nameByNumber.get(number) : null) ||
          c.callerName ||
          null;
        // Show the number on the second line only when a name occupies the first,
        // so an unknown caller isn't printed twice.
        const secondary = [name && number ? number : null, fmt(c.startedAt || c.createdAt)]
          .filter(Boolean)
          .join(' · ');
        return (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onOpen(c.id)}
            className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted ${
              activeId === c.id ? 'bg-muted' : ''
            }`}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {name || number || t('calls.caller')}
              </span>
              <span className="block truncate text-xs text-muted-foreground">{secondary}</span>
            </span>
            {c.urgencyLevel ? (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${urgencyClass(c.urgencyLevel)}`}
              >
                {t(`enums.${c.urgencyLevel}`, c.urgencyLevel)}
              </span>
            ) : null}
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        </li>
        );
      })}
    </ul>
  );
}
